import { globalTranslate } from '@pfsc/shared/locale/app.i18n';
import { toReadableIfNull } from '../utils/toReadableIfNull';
import { USER_ROLE } from './user-role';
import { USER_TYPE } from './user-type';

/**
 * Get user's full name with readable null value supported.
 * @param {BaseUser | null} user User.
 * @returns {string}
 */
export function getFullName(user) {
  if (!user?.firstName && !user?.lastName) {
    return toReadableIfNull(null);
  }
  return user == null ? toReadableIfNull(user) : `${user.firstName} ${user.lastName}`;
}

/**
 * Get user's readable status.
 * @param {User | null} user User.
 * @returns {string}
 */
export function getUserReadableStatus(user) {
  if (user.active === 'Y') {
    return globalTranslate('userStatus.active');
  }
  return globalTranslate('userStatus.inactive');
}

/** Base user. */
export class BaseUser {
  constructor(data) {
    this.id = data.id;
    this.firstName = data.firstName;
    this.lastName = data.lastName;
  }

  get fullName() {
    return getFullName(this);
  }
}

export class Manager extends BaseUser {
  constructor(data) {
    super(data);
    this.hasPreBoughtLaminatedCard = data.hasPreBoughtLaminatedCard;
  }
}

/** User. */
export class User extends BaseUser {
  constructor(data) {
    super(data);
    this.middleName = data.middleName;
    this.jobTitle = data.jobTitle;
    this.dob = data.dob;
    this.language = data.language ? {
      code: data.language.code,
      id: data.language.id,
      name: data.language.name,
    } : null;
    this.lastName = data.lastName;
    this.userName = data.userName;
    this.homeNumber = data.homeNumber;
    this.workNumber = data.workNumber;
    this.faxNumber = data.faxNumber;
    this.isCorporateStudent = data.isCorporateStudent;
    this.isInNOutAdmin = data.isInNOutAdmin;
    this.email = data.email;
    this.createdDate = data.createdDate;
    this.managerId = data.managerId;
    this.isNewSystem = data.isNewSystem;
    this.rbsServerId = data.rbsServerId;
    this.isVisible = data.isVisible;
    this.studentsCount = data.studentsCount;

    /** @type {Manager | null} */
    this.manager = data.manager;
    this.type = data.type ? {
      id: data.type.id,
      name: data.type.name,
      code: data.type.code,
    } : null;
    this.active = data.active;
    this.group = data.group ? {
      id: data.group.id,
      name: data.group.name,
      workNumber: data.group.workNumber,
      email: data.group.email,
      addressId: data.group.addressId,
      size: data.group.size,
      businessLicenseNumber: data.group.businessLicenseNumber,
      createdDate: data.group.createdDate,
      editedDate: data.group.editedDate,
      active: data.group.active,
    } : null;
    this.groupId = data.groupId;
    this.hasPreBoughtLaminatedCard = data.hasPreBoughtLaminatedCard;
    this.address = data.address ? {
      id: data.address.id,
      street: data.address.street,
      street2: data.address.street2,
      city: data.address.city,
      stateCode: data.address.stateCode,
      zip: data.address.zip,
      region: data.address.region,
      county: data.address.county,
    } : null;
    this.registrationStats = data.registrationStats ? {
      classRegistrationCount: data.registrationStats.classRegistrationCount,
      classRegistrationAssignedCount: data.registrationStats.classRegistrationAssignedCount,
      classRegistrationUnassignedCount: data.registrationStats.classRegistrationUnassignedCount,
      proctorURegistrationCount: data.registrationStats.proctorURegistrationCount,
      proctorURegistrationAssignedCount: data.registrationStats.proctorURegistrationAssignedCount,
      proctorURegistrationUnassignedCount: data.registrationStats.proctorURegistrationUnassignedCount,
      eLearningLicenseCount: data.registrationStats.eLearningLicenseCount,
      eLearningLicenseAssignedCount: data.registrationStats.eLearningLicenseAssignedCount,
      eLearningLicenseUnassignedCount: data.registrationStats.eLearningLicenseUnassignedCount,
    } : null;
    this.reminder = data.reminder ? {
      certificateRenewal: data.reminder.certificateRenewal,
      inPersonClass: data.reminder.inPersonClass,
      smsCertificateRenewal: data.reminder.smsCertificateRenewal,
    } : {
      certificateRenewal: 1,
      inPersonClass: 1,
      smsCertificateRenewal: 1,
    };
  }

  get isPfsAdmin() {
    return USER_ROLE.PFS_ADMIN.includes(this.type.id);
  }

  get isAdmin() {
    return USER_TYPE.ADMIN === this.type.id;
  }

  get isGroupAdmin() {
    const isClientAGroupAdmin = USER_TYPE.CLIENT === this.type.id && this.managerId == null;
    return USER_ROLE.GROUP_ADMIN.includes(this.type.id) || isClientAGroupAdmin;
  }

  get isStudent() {
    const hasManager = this.managerId != null;
    const isClientAStudent = USER_TYPE.CLIENT === this.type.id && hasManager;
    return USER_ROLE.STUDENT.includes(this.type.id) || isClientAStudent;
  }

  get isSme() {
    return USER_ROLE.SME.includes(this.type.id);
  }
}

export class UserCreation {
  constructor(data) {
    this.firstName = data.firstName;
    this.middleName = data.middleName;
    this.lastName = data.lastName;
    this.jobTitle = data.jobTitle;
    this.groupId = data.groupId;
    this.hasPreBoughtLaminatedCard = data.hasPreBoughtLaminatedCard;
    this.email = data.email;
    this.phoneNumber = data.phoneNumber;
    this.additionalPhoneNumber = data.additionalPhoneNumber;
    this.street = data.street;
    this.street2 = data.street2;
    this.city = data.city;
    this.stateCode = data.stateCode;
    this.zip = data.zip;
    this.dob = data.dob;
    this.username = data.username;
    this.languageId = data.languageId;
    this.rbsServerId = data.rbsServerId;
    this.companyName = data.companyName;
  }
}

export class UserShortCreation {
  constructor(data) {
    this.firstName = data.firstName;
    this.lastName = data.lastName;
    this.username = data.username;
    this.email = data.email;
    this.groupId = data.groupId;
  }
}

export class UsersUpdate {
  constructor(data) {
    this.userId = data.userId;
    this.dateOfBirth = data.dateOfBirth;
  }
}

export class UserCreationExtend {
  constructor(data) {
    this.username = data.username;
    this.password = data.password;
    this.confirmPassword = data.confirmPassword;
    this.firstName = data.firstName;
    this.middleName = data.middleName;
    this.lastName = data.lastName;
    this.jobTitle = data.jobTitle;
    this.email = data.email;
    this.confirmEmail = data.confirmEmail;
    this.phoneNumber = data.phoneNumber;
    this.groupName = data.groupName;
    this.city = data.city;
    this.mailingAddress = data.mailingAddress;
    this.suite = data.suite;
    this.stateCode = data.stateCode;
    this.zip = data.zip;
    this.dob = data.dob;
    this.employeeId = data.employeeId;
    this.storeNumber = data.storeNumber;
    this.corporateCode = data.corporateCode;
  }
}

/**
 * Get full address.
 * @param {User} user User
 * @returns {string} Example: Fullerton, CA 92833
 */
export function getFullAddress(user) {
  const street = [user?.address?.street, user?.address?.street2].filter(Boolean).join(' ');
  const addressAndZipCode = [user?.address?.stateCode, user?.address?.zip].filter(Boolean).join(' ');
  return [street, addressAndZipCode].filter(Boolean).join(', ');
}
